import { SubmitPAFormBodyProps } from 'components/modals/SubmitPA/submitPA.types';
import {
  MarkCompleteReasons,
  NotPrescribingReason,
  OutcomeDisposition,
  SnoozeDuration
} from 'enums/markCompleteOption';
import { TaskCategories } from 'enums/taskCategories';
import { PARequestStatus } from 'enums/taskDetailsStatus';
import {
  IntakeItemsInfo,
  TaskDetailsProps,
  TaskPatientInfo,
  TaskProps,
  TasksInfoProps,
  TasksSummaryProps
} from 'models/tasks.types';
import { ObjectValues } from 'utils/common/types';

import { PlatformSpecialties } from '../staffs/staffs.types';

export const initialStateTask: {
  intakeItems: IntakeItemsInfo[];
  selectRequireAppointment: boolean;
  taskDetails: TaskDetailsProps;
} = {
  intakeItems: [{ _id: '', active: false, sortNum: 0, taskCategory: '', title: '' }],
  selectRequireAppointment: false,
  taskDetails: {
    _id: '',
    agingAlertAudience: [],
    appointmentInfo: {
      _id: '',
      appointmentDescription: '',
      appointmentMethod: '',
      appointmentStatus: '',
      appointmentTime: {
        endTime: '',
        startTime: ''
      },
      appointmentType: {
        _id: '',
        category: '',
        displayName: ''
      },
      doctorId: '',
      hasPatientJoined: false,
      images: [],
      patientId: '',
      postConsultTasks: [],
      preConsultTaskId: '',
      staffName: '',
      taskId: ''
    },
    category: TaskCategories.Review,
    categoryInfo: null,
    createdAt: '',
    doctorInfo: {
      _id: '',
      name: ''
    },
    dueDate: '',
    intakeItemsInfo: [
      {
        _id: '',
        active: false,
        sortNum: 0,
        taskCategory: '',
        title: ''
      }
    ],
    isAutomated: false,
    isPendingApproval: false,
    medicationImages: [],
    patientDocuments: [],
    patientId: '',
    patientInfo: {
      _id: '',
      dob: '',
      name: '',
      phone: {
        countryCode: '',
        phoneNumber: ''
      },
      state: '',
      stateShortCode: ''
    },
    personalInfo: {
      _id: '',
      dob: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: {
        countryCode: '',
        phoneNumber: ''
      },
      planInfo: {
        _id: '',
        planCode: '',
        planName: '',
        planPrice: 0
      },
      profileImage: '',
      state: '',
      status: '',
      zipCode: ''
    },
    preConsultTaskId: '',
    requestInfo: [],
    requestOrder: {
      createdAt: '',
      orderId: '',
      orderType: '',
      updatedAt: ''
    },
    showBackToCall: false,
    status: 'Inactive',
    upcomingAppointmentInfo: []
  }
};

export const initialApptAsyncInfo = {
  assignedAt: '',
  category: TaskCategories.ApptAsync,
  details: { note: '' },
  doctorInfo: { _id: '', name: '' },
  dueDate: '',
  patientInfo: {
    _id: '',
    dob: '',
    name: '',
    phone: { countryCode: '', phoneNumber: '' },
    state: '',
    stateShortCode: ''
  },
  status: '',
  subRows: []
};

export const initialSteadyMDTaskInfo = {
  assignedAt: '',
  category: TaskCategories.Appointment,
  details: { note: '' },
  doctorInfo: { _id: '', name: '' },
  dueDate: '',
  patientInfo: {
    _id: '',
    dob: '',
    name: '',
    phone: { countryCode: '', phoneNumber: '' },
    state: '',
    stateShortCode: ''
  },
  status: '',
  subRows: []
};

export const initialStateTasks: TasksInfoProps = {
  apptAsyncInfo: initialApptAsyncInfo,
  isSteadyMDTeamMember: false,
  steadyMDTaskInfo: initialSteadyMDTaskInfo,
  tasksSummary: {
    apptAsyncCount: 0,
    asyncCount: 0,
    escalationsCount: 0,
    messageCount: 0,
    queueCount: {
      apptAsync: 0,
      apptAsyncBySpecialty: {},
      steadymd: 0
    },
    renewalCount: 0,
    requestCount: 0,
    requestPACount: 0,
    reviewCount: 0
  }
};

export interface OrderForFollowUp {
  label: string;
  value: string;
}

export interface RequestReason {
  groupLabel: string;
  label: string;
  value: string;
}

export type ApptAsyncInfo = Pick<
  TaskProps,
  | 'assignedAt'
  | 'category'
  | 'details'
  | 'dueDate'
  | 'subRows'
  | 'patientInfo'
  | 'doctorInfo'
  | 'status'
>;

export type SteadyMDTaskInfo = Pick<
  TaskProps,
  | 'assignedAt'
  | 'category'
  | 'details'
  | 'dueDate'
  | 'subRows'
  | 'patientInfo'
  | 'doctorInfo'
  | 'status'
>;

export interface CreateNewTaskParams {
  assignedTo?: string;
  audience?: string;
  dueDate?: string;
  elationPrescriptionId?: string;
  isUrgent?: boolean;
  note: string;
  orderForFollowUp?: OrderForFollowUp;
  parentTaskId?: string;
  patientId: string;
  requestReason?: RequestReason[];
  tags?: string[];
  timezone?: string;
  tpsBenefit?: {
    drug_name: string;
    insurance_name: string;
    request_id: string;
  };
}

interface TeamsProps {
  id: string;
  name?: string;
}

export interface CreateRequestOrderTaskParams {
  dueDate?: string;
  elationPrescriptionId?: number;
  note: string;
  patientId: string;
  reasons?: string[];
  teams?: TeamsProps[];
}

export interface DiagnosisCodesProps {
  _id?: string;
  code: string;
  createdAt?: string;
  description?: string;
  updatedAt?: string;
}

export interface UpdatedTaskResponseProps {
  taskAudience?: string[];
  taskId: string;
  updatedData: Partial<TaskProps>;
}

export interface AvailableStaffWarning {
  canAssignStaff?: boolean;
  message: string;
  shouldConfirm?: boolean;
}

export interface AvailableStaffResponse {
  _id: string;
  isClinicalLeadership?: boolean;
  name: string;
  platformSpecialties?: PlatformSpecialties[];
  profileImage: string;
  userType: {
    name: string;
    shortCode: 'MD' | 'NP' | 'MA' | 'CS' | 'AD' | 'PH';
  };
  warning: AvailableStaffWarning;
}

export interface AvailableStaffProps {
  limit?: number;
  pageNo?: number;
  searchKey?: string;
  timezone?: string;
}

export interface AvailableStaffForPatientProps {
  isUrgentTask?: boolean;
  patientId?: string;
  searchKey?: string;
  taskCategory: string;
  timezone?: string;
}

export interface RequestPAProps {
  assignedTo?: string;
  audience?: string;
  isUrgent: boolean;
  medication?: string;
  note: string;
  patientUserId: string;
  timezone?: string;
  tpsBenefit?: { drug_name: string; insurance_name: string; request_id: string };
}

export type ChangeDispositionRequestProps = {
  body: {
    disposition?: MarkCompleteReasons | OutcomeDisposition;
    expectedRevision?: number;
    note?: string;
    notPrescribingNote?: string;
    notPrescribingReason?: NotPrescribingReason;
    snoozeDuration?: SnoozeDuration;
  };
  taskId: string;
};

export type SubmitPAFormRequestProps = {
  body: SubmitPAFormBodyProps;
  taskId: string;
};

export type AssignTaskToTeamRequestProps = {
  taskId: string;
  teamIds?: string[];
};

export type AsyncScheduleDetails = {
  active: number;
  additionalInfoRequired: number;
  averageWaitingTime: number;
  completedNoRx: number;
  longestWaitingTime: number;
  new: number;
  prescribed: number;
};

export type ApptAsyncScheduleDetails = {
  active: number;
  additionalInfoRequired: number;
  averageWaitingTime: number;
  completedNoRx: number;
  dueTasksCount: number;
  incomplete: number;
  longestWaitingTime: number;
  new: number;
  overdueTasksCount: number;
  prescribed: number;
};

export type ChangePAStatusProps = {
  drugName?: string;
  insuranceName?: string;
  patientId?: string;
  requestId?: string;
  status: PARequestStatus;
  taskId?: string;
};

export type EscalateTaskRequestProps = {
  isConfirmed?: boolean;
  note?: string;
  taskId: string;
  teamIds?: string[];
  userId?: string;
};

export type TaskTagsType = {
  _id: string;
  category: string;
  name: string;
};

export interface RequestOrderOptionsProps {
  groupLabel: string;
  label: string;
  toEscalate?: boolean;
  value: string;
}

export type GroupRequestOrderOptionsProps = {
  label: string;
  options: RequestOrderOptionsProps[];
  toEscalate?: boolean;
};

interface TaskOrderReasons {
  categoryName: string;
  key: string;
  name: string;
}

export interface TaskRequestOrderReasonsType {
  categoryName: string;
  displayOrder: number;
  reasons: TaskOrderReasons[];
  toEscalate: boolean;
}

export const AssignValueType = {
  primary: 'assign-primary-provider',
  releaseToList: 'release-to-list',
  role: 'assign-role',
  staff: 'assign-staff-member',
  takeOver: 'takeover',
  team: 'assign-teams'
} as const;

export type AssignValueTypes = ObjectValues<typeof AssignValueType>;

export type BulkEditTaskParams = {
  taskIds: string[];
  updateData: {
    audience?: string[];
    operationType: AssignValueTypes;
    staffUserId?: string;
    teamIds?: string[];
    timezone?: string;
  };
};

export type BulkEditUpdatedResponseItem = {
  category: string;
  createdAt?: string;
  dueDate?: string;
  patientInfo: TaskPatientInfo;
  taskId: string;
};

export type BulkEditFailedResponseItem = BulkEditUpdatedResponseItem & {
  reason: string;
};

export type BulkEditTaskResponse = {
  data: {
    failed: BulkEditFailedResponseItem[];
    updated: BulkEditUpdatedResponseItem[];
  };
  message: string;
};

export type ReviewRejectedLabAction = 'rejected' | 'approved';

export type ReviewRejectedLabRequest = {
  action: ReviewRejectedLabAction;
  orderId: string;
  reason: string;
  taskId: string;
};

export type TaskRequestProps = {
  isSteadyMDTeamMember?: boolean;
  summary?: TasksSummaryProps;
  tasks: TaskProps[];
  totalCount: number;
};

export type CreateReminderResponse = {
  message: string;
};

export type TaskStatisticsStaffItem = {
  avgResolutionMinutes: number;
  completedCount: number;
  role: string;
  staffId: string;
  staffName: string;
};

export type TaskStatisticsResponse = {
  avgResolutionMinutes: number;
  staffBreakdown: TaskStatisticsStaffItem[];
  totalCompleted: number;
};
